<template>
  <div>
    <app-spinner v-if="!is_loaded"/>
    <template v-else>
      <app-cells>
        <h1 class="title">List of employees</h1>
        <app-button @click.native="onShowModal">Create employee</app-button>
      </app-cells>
      <app-page-container custom="1210px" min-height="auto">
        <app-flex-grid :rows="[['180px', '250px', '220px', 1, 'auto']]">
          <template #col-1-1>
            <app-input
              @input.native="onUsersSearch"
              v-model="search"
              type="search"
              placeholder="Search by name"
              name="search"
            />
          </template>
          <template #col-1-2>
            <app-select
              @input="onChangeFilter"
              v-model="company_filter"
              :options="company_options"
              :reduce="item => item.code"
              placeholder="Company"
            />
          </template>
          <template #col-1-3>
            <app-select
              @input="onChangeFilter"
              v-model="department_filter"
              :options="department_options"
              :reduce="item => item.code"
              placeholder="Department"
            />
          </template>
          <template #col-1-4>
            <app-select
              @input="onChangeFilter"
              v-model="position_filter"
              :reduce="item => item.code"
              :options="position_options"
              placeholder="Position"
            />
          </template>
          <template #col-1-5>
            <app-filters
              @change="fetchItems"
              v-model="status_filter"
              :filters="status_filters"
              name="filterStatus"
            />
          </template>
        </app-flex-grid>
      </app-page-container>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader }"
      >
        <template #h__name>
          <div class="table-default__left">Name</div>
        </template>
        <template #h__company>
          <div class="table-default__left">Company</div>
        </template>
        <template #h__position>
          <div class="table-default__left">Position</div>
        </template>
        <template #h__employment_status>
          <div class="table-default__left">Status</div>
        </template>
        <template #name="{ row }">
          <div class="table-default__left table-default__left--photo">
            <div class="table-default__photo">
              <img :src="getUserAvatar(row.crop.crop_url, row.gender?.code || null)" alt="Avatar">
            </div>
            <router-link :to="{ name: 'employees-profile', params: { id: row.id } }" class="table-default__link-border">
              {{ row.first_name }} {{ row.last_name }}
            </router-link>
          </div>
        </template>
        <template #communication="{ row }">
          <div class="table-default__controls">
            <button
              v-tooltip="{content: row.email, placement: 'top-start', offset: 10}"
              @click="onCopyContactLink(row.email)"
              class="table-default__activity table-default__activity--mail"
              :class="{'table-default__activity--active': row.email}"
            >
            </button>
            <button
              v-tooltip="{content: row.phone, placement: 'top-start', offset: 10}"
              @click="onCopyContactLink(row.phone)"
              class="table-default__activity table-default__activity--phone"
              :class="{'table-default__activity--active': row.phone}"
            >
            </button>
            <a
              :href="`https://t.me/${row.phone}`"
              target="_blank"
              class="table-default__activity table-default__activity--tg"
              :class="{'table-default__activity--active': row.phone_telegram}"
            >
            </a>
            <a
              :href="`https://wa.me/${row.phone}`"
              target="_blank"
              class="table-default__activity table-default__activity--wa"
              :class="{'table-default__activity--active': row.phone_whatsapp}"
            >
            </a>
          </div>
        </template>
        <template #position="{ row }">
          <div class="table-default__left">{{ row.position.name }} ({{ row.seniority }})</div>
        </template>
        <template #salary="{ row }">
          <template v-if="row.salary">
            {{ amountFormat(row.salary.gross) }} TZS
          </template>
          <template v-else>-</template>
        </template>
        <template #national_id="{ row }">
          <div class="table-default__controls">
            <app-document-icon
              name="national_id"
              :fields="{ id: row.national_id, expire: row.national_id_expire, scan_copy: row.national_id_scancopy }"
              :first_name="row.first_name"
              :last_name="row.last_name"
            />
            <app-document-icon name="voting_card" :fields="{ id: row.voting_card }"/>
            <app-document-icon
              name="driver_licence"
              :fields="{ id: row.driver_licence, expire: row.driver_licence_expire, scan_copy: row.driver_licence_scancopy }"
              :first_name="row.first_name"
              :last_name="row.last_name"
            />
            <app-document-icon
              name="tin"
              :fields="{ id: row.tin, scan_copy: row.tin_scancopy }"
              :first_name="row.first_name"
              :last_name="row.last_name"
            />
            <app-document-icon
              name="nssf_id"
              :fields="{ id: row.nssf_id, scan_copy: row.nssf_id_scancopy }"
              :first_name="row.first_name"
              :last_name="row.last_name"
            />
            <app-document-icon
              name="nhif_id"
              :fields="{ id: row.nhif_id, scan_copy: row.nhif_id_scancopy }"
              :first_name="row.first_name"
              :last_name="row.last_name"
            />
            <app-document-icon name="bank_account" :fields="{ id: row.bank_account, bank_name: row.bank }"/>
          </div>
        </template>
        <template #warnings="{ row }">{{ row.warnings ? row.warnings.length : '-' }}</template>
      </v-client-table>
      <app-cells :position="pagination.pages > 1 ? 'between' : 'end'">
        <paginate
          :clickHandler="onClickPagination"
          v-if="pagination.pages > 1"
          v-model="pagination.page"
          :page-count="pagination.pages"
          :prev-text="'<'"
          :next-text="'>'"
          :container-class="'pagination'"
          :page-class="'pagination__item'"
          :page-link-class="'pagination__link'"
          :prev-class="'pagination__item pagination__item--prev'"
          :next-class="'pagination__item pagination__item--next'"
          :next-link-class="'pagination__link pagination__link--next'"
          :prev-link-class="'pagination__link pagination__link--prev'"
        />
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page"/>
      </app-cells>
    </template>
    <modal
      name="client-form"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('client-form')" class="popup__close" type="button"></button>
        <div class="popup__title">New employee</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-form-group label="First name" label-for="first_name" required>
              <app-input
                @change.native="$v.form.first_name.$touch()"
                v-model="form.first_name"
                :error="$v.form.first_name.$error"
                id="first_name"
                placeholder="First name"
                name="first_name"
              />
              <template #error>
                <div v-if="$v.form.first_name.$dirty && !$v.form.first_name.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Middle name" label-for="middle_name">
              <app-input
                v-model="form.middle_name"
                id="middle_name"
                placeholder="Middle name"
                name="middle_name"
              />
            </app-form-group>
            <app-form-group label="Last name" label-for="last_name" required>
              <app-input
                @change.native="$v.form.last_name.$touch()"
                v-model="form.last_name"
                :error="$v.form.last_name.$error"
                id="last_name"
                placeholder="Last name"
                name="last_name"
              />
              <template #error>
                <div v-if="$v.form.last_name.$dirty && !$v.form.last_name.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Gender" class="form-group--center" required>
              <app-cells :indent="false">
                <app-radio
                  v-for="item in genders_options"
                  :key="item.code"
                  v-model.number="form.gender"
                  :value="item.code"
                  name="gender"
                  :checked="form.gender"
                  :error="$v.form.gender.$error"
                >
                  <template #label>{{ item.name }}</template>
                </app-radio>
              </app-cells>
              <template #error>
                <div v-if="$v.form.gender.$dirty && !$v.form.gender.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Department" required>
              <app-select
                @input="onDepartmentChange"
                v-model="form.department"
                :options="department_options"
                :clearable="false"
                placeholder="Company"
                :error="$v.form.department.$error"
              />
              <template #error>
                <div v-if="$v.form.department.$dirty && !$v.form.department.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Position" required>
              <app-select
                v-model="form.position"
                :reduce="item => item.code"
                :options="form.department?.positions || []"
                :clearable="false"
                placeholder="Position"
                :error="$v.form.position.$error"
                :readonly="!form.department"
              />
              <template #error>
                <div v-if="$v.form.position.$dirty && !$v.form.position.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button :disabled="$v.form.$error" type="submit" ref="submit">Create employee</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  getAdministrativeLegalEntities,
  getEmployeesDepartmentList,
  getEmployeesList,
  getEmployeesPositionList,
  getGenders,
  postEmployee,
} from '@/http'
import AppDocumentIcon from '@/components/AppDocumentIcon'
import debounce from 'lodash/debounce'
import { required } from 'vuelidate/lib/validators'
import { format, parse } from 'date-fns'

export default {
  name: 'EmployeesList',
  components: { AppDocumentIcon },
  data() {
    return {
      is_loaded: false,
      search: '',
      table: {
        items: [],
        columns: [
          'name',
          'communication',
          'position',
          'salary',
          'national_id',
          'warnings',
        ],
        options: {
          headings: {
            'name': 'Name',
            'communication': 'Communication',
            'position': 'Position',
            'salary': 'Salary',
            'national_id': 'Profile',
            'warnings': 'Warnings',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries'
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        }
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1
      },
      form: {
        department: null,
        position: null
      },
      genders_options: [],
      company_options: [],
      position_options: [],
      department_options: [],
      company_filter: '',
      department_filter: '',
      position_filter: '',
      table_preloader: false,
      status_filter: 'working',
      status_filters: [
        { value: 'working', label: 'Working' },
        { value: 'terminated', label: 'Terminated' }
      ],
      pad: null
    }
  },
  validations: {
    form: {
      first_name: { required },
      last_name: { required },
      gender: { required },
      department: { required },
      position: { required }
    }
  },
  created() {
    this.fetchItems()
    getAdministrativeLegalEntities({ employment: true })
      .then(res => { this.company_options = res.data })
    getEmployeesDepartmentList().then(res => { this.department_options = res.data })
    getEmployeesPositionList().then(res => { this.position_options = res.data })
    getGenders().then(res => { this.genders_options = res.data })
  },
  methods: {
    fetchItems() {
      getEmployeesList({
        page: this.pagination.page,
        page_size: 20,
        search: this.search,
        company: this.search ? '' : this.company_filter,
        department: this.search ? '' : this.department_filter,
        position: this.search ? '' : this.position_filter,
        employment_status: this.status_filter === 'working' ? 'working' : '',
        employment_status_exclude: this.status_filter === 'terminated' ? 'working' : ''
      }).then(res => {
        this.table.items = res.data.results
        this.pagination.pages = res.data.pages
        this.pagination.count = res.data.count
        this.is_loaded = true
        this.table_preloader = false
      })
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly'
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      postEmployee(this.normalizeForm())
        .finally(() => { this.$refs.submit.preload = false })
        .then(res => {
          this.$notify({
            type: 'success',
            title: 'Employee created'
          })
          this.$router.push({ name: 'employees-profile', params: { id: res.data.id } })
        })
    },
    normalizeForm() {
      const form = { ...this.form }

      form.department = form.department.code
      return form
    },
    onDepartmentChange() {
      this.form.position = null
    },
    onChangeFilter() {
      this.pagination.page = 1
      this.search = ''
      this.fetchItems()
    },
    onShowModal() {
      this.form = {
        department: null,
        position: null
      }
      this.$v.form.$reset()
      this.$modal.show('client-form')
    },
    onClickPagination(page) {
      this.table_preloader = true
      this.pagination.page = page
      this.fetchItems()
    },
    dateFormat(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : ''
    },
    onUsersSearch() {
      this.searchUsers(this)
    },
    searchUsers: debounce(vm => {
      vm.pagination.page = 1
      vm.fetchItems()
    }, 350),
    onCopyContactLink(link) {
      navigator.clipboard.writeText(link)
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Copied to clipboard'
          })
        })
        .catch(() => {
          console.error('Something went wrong')
        })
    },
    getUserAvatar(crop_url, gender) {
      if (crop_url) return crop_url
      else {
        if (!gender) {
          return require('@/assets/img/temp/avatar-slug.jpg')
        } else if (gender === 'male') {
          return require('@/assets/img/temp/avatar-slug-male.jpg')
        } else if (gender === 'female') {
          return require('@/assets/img/temp/avatar-slug-female.jpg')
        }
      }
    },
    amountFormat(amount) {
      return Intl.NumberFormat('ru-RU').format(amount)
    },
  }
}
</script>
<style lang="sass" scoped>
.family-list
  display: flex
  align-items: flex-end
  justify-content: center
  gap: 14px

.family-list__item
  img
    margin-right: 5px

  span
    width: 10px
</style>
